import type { FilterConditionPlugin } from './filter';
import type { ShareableType } from '@bindoc-projects/resource-domain-lib';
import type { OverrideProperty } from '@bindoc-projects/util-domain-lib';
import type { BdCondition } from '@bindoc-projects/condition-domain-lib';
import type { FilterDetailedText } from './filter-detailed-text';
// Type-only to keep the token ↔ plugin dependency cycle erased at runtime.
import type { FilterRegistrationToken } from './token';

export type FilterConditionPluginSpecificationType = string;

export interface FilterConditionPluginSpecification<
  T,
  TObject = { type: string },
  TConditionObject = unknown,
> {
  type: FilterConditionPluginSpecificationType;
  category: 'case' | 'person' | 'clinic';
  displayName: { [lang: string]: string };
  sortingIndex: number;
  occurrence: 'single' | 'multiple';

  fromObject(obj: TObject): FilterConditionPluginImpl<T>;

  fromCondition(jsonObj: TConditionObject): FilterConditionPluginImpl<T>;

  /**
   * Optional async rehydration for conditions that carry a NESTED condition tree
   * which must itself be deserialized against the registrations (the
   * department/deqs wrappers). When present, `FilterConditionUtil.fromConditionJson`
   * calls this instead of the synchronous {@link fromCondition}, threading the
   * full registration set so the inner tree can be reconstructed.
   */
  fromConditionAsync?(
    jsonObj: TConditionObject,
    registeredFilter: FilterRegistrationToken[],
  ): Promise<FilterConditionPluginImpl<T>>;

  newInstance(): FilterConditionPluginImpl<T>;
}

export type FilterPluginSpecificationRestriction = {
  specification: FilterConditionPluginSpecification<any>;
  permissions?: `${string}:${string}`[];
  needsAuthentication?: boolean;
}[];

export abstract class FilterConditionPluginImpl<
  TValue,
  TJsonObject extends { type: string } = { type: string },
> implements FilterConditionPlugin<TValue>
{
  static Specification: FilterConditionPluginSpecification<any>;

  protected constructor(public type: string) {}

  abstract clone(): FilterConditionPlugin<TValue>;

  abstract toCondition(): BdCondition | null;

  abstract toJSON(): TJsonObject | null;

  abstract toText(): string | null;

  abstract toValue(): TValue;

  abstract validationToText(): string;

  public equals(other: FilterConditionPlugin<TValue>): boolean {
    return !!other && this.type === other.type;
  }

  public valid(): boolean {
    const value = this.toValue();
    if (value instanceof Array) {
      return !!value && value.length > 0;
    } else {
      return !!value;
    }
  }


  public toDetailedText(resolvedNames?: Map<string, string>): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    return { title, items: this._extractDetailItems(resolvedNames) };
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected _extractDetailItems(_resolvedNames?: Map<string, string>): string[] {
    const value = this.toValue();
    if (value == null) return [];
    if (Array.isArray(value)) {
      return value.map((v: unknown) => {
        if (typeof v === 'string' || typeof v === 'number') return String(v);
        if (v && typeof v === 'object' && 'name' in v) {
          const name = (v as { name: string }).name;
          const desc =
            'description' in v && (v as { description?: string }).description
              ? ` - ${(v as { description: string }).description}`
              : '';
          return `${name}${desc}`;
        }
        return String(v);
      });
    }
    if (typeof value === 'string' || typeof value === 'number') return [String(value)];
    return [];
  }

  public hasRelativeTimeSelection(): boolean {
    return false;
  }

  public getChildResources(): { [resourceName in ShareableType]?: string[] } {
    return {};
  }

  public getExtraChildResources(): Record<string, string[]> {
    return {};
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public overrideClone(override: OverrideProperty[]): FilterConditionPlugin<number[]> {
    return this.clone();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public getOverrideProperties(tileId: string, dependencyVirtualClinicId: string): OverrideProperty[] {
    return [];
  }
}
